/**
 * Bridges a summary-rule `when` map (`{ column: [allowed values] }`, an implicit
 * AND of "column is any of […]") to the QueryBuilder tree so the rule conditions
 * are edited with the same builder used by run_when / preconditions.
 *
 * The `when` map only encodes the `in` semantics, so the builder exposes a single
 * `in` ("is any of") operator per field — that keeps the round-trip lossless.
 */
import { v4 as uuidv4 } from "uuid";
import { createEmptyGroup } from "../../../../../components/queryBuilder/queryBuilderUtils";
import {
  FieldDefinition,
  QueryBuilderConfig,
  QueryCondition,
  QueryGroup,
  QueryNode,
} from "../../../../../data-model/queryBuilder";

/**
 * Builds a QueryBuilder config from the summary source columns. Each column is a
 * free-text STRING field with a single `is any of` operator; values are entered
 * via the CMM-backed multi-select (keyed on the column name).
 *
 * @param columns - Selectable column names (summary source columns + used ones)
 * @returns QueryBuilder field config
 */
export function buildSummaryWhenConfig(columns: string[]): QueryBuilderConfig {
  const fields: FieldDefinition[] = columns.map((column) => ({
    value: column,
    label: column,
    dataType: "STRING",
    operators: [
      { value: "in", label: "is any of", valueInputType: "multi-select" },
    ],
  }));
  return { fields };
}

/** Converts a `when` map into a QueryBuilder AND group. */
export function whenToQueryValue(when: Record<string, string[]>): QueryGroup {
  const entries = Object.entries(when ?? {});
  if (entries.length === 0) {
    return createEmptyGroup();
  }
  return {
    type: "group",
    id: uuidv4(),
    conjunction: "AND",
    children: entries.map(([field, values]) => ({
      type: "condition",
      id: uuidv4(),
      field,
      operator: "in",
      value: values,
    })),
  };
}

function collectConditions(node: QueryNode, out: QueryCondition[]): void {
  if (node.type === "group") {
    node.children.forEach((child) => collectConditions(child, out));
  } else {
    out.push(node);
  }
}

/**
 * Converts a QueryBuilder group back into a `when` map, collecting every leaf
 * condition (group nesting/conjunction is flattened since `when` is implicit AND).
 * Blank fields are dropped; the last condition wins on a duplicate column.
 *
 * @param group - QueryBuilder group value
 * @returns `when` map of column -> allowed values
 */
export function queryValueToWhen(group: QueryGroup): Record<string, string[]> {
  const conditions: QueryCondition[] = [];
  collectConditions(group, conditions);
  const result: Record<string, string[]> = {};
  for (const cond of conditions) {
    const field = cond.field.trim();
    if (!field) continue;
    const values = Array.isArray(cond.value)
      ? cond.value
      : cond.value
        ? [cond.value]
        : [];
    result[field] = values;
  }
  return result;
}
